/**
 * Persistent model library, backed by IndexedDB.
 *
 * Metadata and file bytes live in separate object stores. Listing the library is the
 * common operation and only needs metadata, so keeping the blobs out of that record means
 * rendering the sidebar never pulls tens of megabytes of geometry into memory.
 *
 * A third store holds tombstones — models deliberately deleted. They exist so that a
 * deletion can travel to the account's other sessions instead of being quietly undone by
 * the next share, and they are never pruned: a forgotten tombstone is a model that comes
 * back from the dead. They are small, being a hash and a timestamp.
 *
 * The queries that filter by account or by hash read the whole metadata store and filter
 * in memory rather than using an index. A library is tens or hundreds of records of a few
 * hundred bytes each, so an index would buy nothing measurable and would cost a migration
 * and a set of range queries to get wrong.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

import { normaliseColour } from '../domain/colour.js';
import { hashBytes } from '../domain/hash.js';
import { normaliseRoomScale } from '../domain/nudge.js';
import {
  mergePresentation,
  presentationOf,
  type Amendment,
  type Manifest,
  type ManifestEntry,
  type ModelPresentation,
  type Tombstone,
} from '../domain/sync.js';
import type {
  Bounds,
  DeletedModel,
  FileColourFormat,
  ModelId,
  ModelMeta,
  ModelStats,
} from '../domain/types.js';
import { logger } from '../log.js';

const log = logger('models');

const DB_NAME = '3dpreview';

/**
 * Version 2 added the tombstone store, and `hash` and `owner` to the metadata record.
 * Version 3 added the thumbnail store.
 *
 * The two metadata fields need no migration step: they are optional, absent means what it
 * meant before they existed, and they are filled in when first needed. Nor does the
 * thumbnail store — a picture is derived from a file that is already here, so an empty one
 * is a store that fills itself in rather than one missing anything.
 */
const DB_VERSION = 3;

const META_STORE = 'meta';
const FILE_STORE = 'files';
const TOMBSTONE_STORE = 'tombstones';
const THUMB_STORE = 'thumbs';
const CREATED_AT_INDEX = 'createdAt';

/** What `owner` is for a model no account has claimed. Compound keys cannot be undefined. */
const NOBODY = '';

interface PreviewSchema extends DBSchema {
  [META_STORE]: {
    key: ModelId;
    value: ModelMeta;
    indexes: { [CREATED_AT_INDEX]: number };
  };
  [FILE_STORE]: {
    key: ModelId;
    value: Blob;
  };
  [TOMBSTONE_STORE]: {
    key: [string, string];
    value: DeletedModel;
  };
  [THUMB_STORE]: {
    key: ModelId;
    value: StoredThumbnail;
  };
}

/**
 * A picture of a model, and the colour it was taken in.
 *
 * The colour is kept so the picture can be recognised as out of date. Recolouring a model
 * does not change its shape, but it does change what it looks like, and a sidebar showing
 * the old colour beside a view showing the new one is a sidebar disagreeing with itself.
 *
 * Kept out of the metadata record deliberately, for two reasons. A record is the portable
 * description of a model — the shape a backend would agree on — and a PNG is not part of
 * that; and `list()` reads every record on every change of anything, which is no place for
 * a few hundred kilobytes of pictures nobody has asked to see.
 */
export interface StoredThumbnail {
  readonly colour: string;
  readonly image: Blob;
}

/** Raised when the browser refuses a write because the origin is out of space. */
export class StorageFullError extends Error {
  constructor(cause: unknown) {
    super('There is not enough browser storage left to save this model.');
    this.name = 'StorageFullError';
    this.cause = cause;
  }
}

export interface StorageUsage {
  readonly usedBytes: number;
  readonly quotaBytes: number | undefined;
}

/** Extra facts about a model being saved, all of which have a sensible absence. */
export interface AddOptions {
  /** The account to attribute it to. Absent while signed out. */
  readonly owner?: string | undefined;
  /**
   * The file's bytes, if the caller has already read them.
   *
   * Saving a model needs its hash, and the caller has invariably just read the whole file
   * in order to validate it. Passing that buffer back avoids reading it from disk twice.
   */
  readonly bytes?: ArrayBuffer | undefined;
}

export class ModelStore {
  private constructor(private readonly db: IDBPDatabase<PreviewSchema>) {}

  static async open(name: string = DB_NAME): Promise<ModelStore> {
    const db = await openDB<PreviewSchema>(name, DB_VERSION, {
      upgrade(database, oldVersion) {
        if (oldVersion < 1) {
          const meta = database.createObjectStore(META_STORE, { keyPath: 'id' });
          meta.createIndex(CREATED_AT_INDEX, 'createdAt');
          database.createObjectStore(FILE_STORE);
        }
        if (oldVersion < 2) {
          // Keyed by account and hash together, so that two people using one browser can
          // each delete the same file without either erasing the other's record of it.
          database.createObjectStore(TOMBSTONE_STORE, { keyPath: ['owner', 'hash'] });
        }
        if (oldVersion < 3) {
          database.createObjectStore(THUMB_STORE);
        }
      },
    });

    return new ModelStore(db);
  }

  /**
   * Save a validated STL and return the metadata record now describing it.
   *
   * Both stores are written in one transaction, so a failure part way through cannot
   * leave a listed model whose bytes are missing.
   */
  async add(
    name: string,
    blob: Blob,
    stats: ModelStats,
    options: AddOptions = {},
  ): Promise<ModelMeta> {
    const hash = await hashBytes(options.bytes ?? (await blob.arrayBuffer()));

    const meta: ModelMeta = {
      ...stats,
      id: newId(),
      name,
      byteSize: blob.size,
      createdAt: Date.now(),
      hash,
      ...(options.owner === undefined ? {} : { owner: options.owner }),
    };

    await this.write(meta, blob);
    return meta;
  }

  /**
   * Save a model that arrived from another session.
   *
   * Distinct from [`add`] in three ways that matter: the hash is the one already agreed
   * with the peer rather than one computed here, the creation time is the original
   * addition rather than now — a copy that claimed to be newer than the deletion it came
   * before would undo that deletion at the next share — and the owner is required, because
   * there is no way to receive a model except as a signed-in account.
   *
   * The caller is responsible for having checked that the bytes really do hash to
   * `entry.hash` and really do parse as an STL. Both checks live in the transfer code,
   * next to the connection the bytes came off.
   */
  async importModel(
    entry: ManifestEntry,
    blob: Blob,
    stats: ModelStats,
    owner: string,
  ): Promise<ModelMeta> {
    const meta: ModelMeta = {
      ...stats,
      id: newId(),
      name: entry.name,
      byteSize: blob.size,
      createdAt: entry.addedAt,
      hash: entry.hash,
      owner,
      // With the times they were decided at, so that this copy can take part in the next
      // reconciliation on equal terms rather than looking like a session with no opinion.
      ...recorded(storable(entry)),
    };

    await this.write(meta, blob);
    return meta;
  }

  private async write(meta: ModelMeta, blob: Blob): Promise<void> {
    try {
      const tx = this.db.transaction([META_STORE, FILE_STORE], 'readwrite');
      await Promise.all([
        tx.objectStore(META_STORE).put(meta),
        tx.objectStore(FILE_STORE).put(blob, meta.id),
        tx.done,
      ]);
    } catch (error) {
      if (isQuotaError(error)) throw new StorageFullError(error);
      throw error;
    }
  }

  /** Every stored model, newest first. Ownership is not consulted: this is the library. */
  async list(): Promise<ModelMeta[]> {
    const byOldest = await this.db.getAllFromIndex(META_STORE, CREATED_AT_INDEX);
    return byOldest.reverse();
  }

  async get(id: ModelId): Promise<ModelMeta | undefined> {
    return this.db.get(META_STORE, id);
  }

  /** The picture held for a model, if one has been taken. */
  async getThumbnail(id: ModelId): Promise<StoredThumbnail | undefined> {
    return this.db.get(THUMB_STORE, id);
  }

  /**
   * Keep a picture of a model.
   *
   * Quietly, unlike everything else written here. A full disk is worth reporting when it
   * costs somebody the model they were adding; it is not worth reporting when it costs them
   * a thumbnail they never asked for, and the square falls back to what it shows before a
   * picture arrives.
   */
  async putThumbnail(id: ModelId, thumbnail: StoredThumbnail): Promise<void> {
    try {
      await this.db.put(THUMB_STORE, thumbnail, id);
    } catch (error) {
      log.warn('A thumbnail could not be stored.', error);
    }
  }

  /**
   * Record the colour a model should be rendered in.
   *
   * Read-modify-write in one transaction rather than a partial update, because IndexedDB
   * has no such thing as a partial update — `put` replaces the whole record, so reading it
   * inside the same transaction is what stops a concurrent write being clobbered.
   *
   * Returns the updated record, or `undefined` if the model has since been deleted, which
   * is what happens if it is removed while its colour is being chosen.
   */
  async setColour(id: ModelId, colour: string): Promise<ModelMeta | undefined> {
    const normalised = normaliseColour(colour);
    if (normalised === undefined) {
      throw new TypeError(`${colour} is not a colour this can store.`);
    }

    try {
      const tx = this.db.transaction(META_STORE, 'readwrite');
      const store = tx.objectStore(META_STORE);

      const existing = await store.get(id);
      if (!existing) {
        await tx.done;
        return undefined;
      }

      // Stamped, so that another session holding the same file can tell whose choice came
      // later. See `ModelPresentation` in `sync`.
      const updated: ModelMeta = {
        ...existing,
        colour: normalised,
        colourAt: Date.now(),
      };
      await store.put(updated);
      await tx.done;

      return updated;
    } catch (error) {
      if (isQuotaError(error)) throw new StorageFullError(error);
      throw error;
    }
  }

  /**
   * Record what a file turned out to say about its own colours.
   *
   * Separate from `setColour`, which stores a choice somebody made. This stores a fact
   * about the bytes, worked out the first time a model stored before the field existed is
   * read for display, so that the answer is not recomputed on every subsequent one.
   */
  /**
   * Correct a model's recorded extent.
   *
   * Written when what was built from a file disagrees with what was stored about it, which
   * happens when the rule for reading a format changes — a glTF measured while its units
   * were read as millimetres describes a model a thousand times smaller than the one now
   * drawn from the same bytes. The file is the truth; the record is a note about it, and
   * a note that has gone stale is simply rewritten.
   */
  async setBounds(id: ModelId, bounds: Bounds): Promise<ModelMeta | undefined> {
    return this.amend(id, (existing) => ({ ...existing, bounds }));
  }

  async setFileColours(
    id: ModelId,
    fileColours: FileColourFormat,
  ): Promise<ModelMeta | undefined> {
    return this.amend(id, (existing) => ({ ...existing, fileColours }));
  }

  /**
   * Record how large a model should stand in the room, as a multiple of true size.
   *
   * Refuses a figure outside what the resize gesture can produce rather than clamping it:
   * a value that far out did not come from this application, and storing a corrected
   * version of it would be inventing an intention.
   */
  async setRoomScale(id: ModelId, roomScale: number): Promise<ModelMeta | undefined> {
    const normalised = normaliseRoomScale(roomScale);
    if (normalised === undefined) {
      throw new RangeError(`${String(roomScale)} is not a size this can store.`);
    }

    return this.amend(id, (existing) => ({
      ...existing,
      roomScale: normalised,
      roomScaleAt: Date.now(),
    }));
  }

  /**
   * Apply changes to how models are shown, decided in another session.
   *
   * Field by field, and only where the incoming decision is demonstrably the later one.
   * The comparison is made again here rather than trusted from the sender: the plan that
   * produced these was drawn up before the transfer began, and this session may have
   * recoloured something in the meantime.
   *
   * Returns the ids that actually changed, so that a model on display can be repainted and
   * one standing in the room resized, rather than leaving the view disagreeing with the
   * library until something else happens to refresh it.
   */
  async applyAmendments(
    amendments: readonly Amendment[],
    owner: string,
  ): Promise<ModelId[]> {
    if (amendments.length === 0) return [];

    const byHash = new Map(amendments.map((change) => [change.hash, change] as const));

    const tx = this.db.transaction(META_STORE, 'readwrite');
    const store = tx.objectStore(META_STORE);
    const changed: ModelId[] = [];

    for (const meta of await store.getAll()) {
      const change = meta.hash === undefined ? undefined : byHash.get(meta.hash);
      // Somebody else's copy of the same file is not ours to restyle.
      if (!change || (meta.owner ?? NOBODY) !== owner) continue;

      const merged = mergePresentation(meta, storable(change));
      if (!differs(meta, merged)) continue;

      await store.put({ ...meta, ...recorded(merged) });
      changed.push(meta.id);
    }

    await tx.done;
    return changed;
  }

  /**
   * Read a record, change it, and write it back in one transaction.
   *
   * IndexedDB has no partial update — `put` replaces the whole record — so reading it
   * inside the same transaction is what stops a concurrent write being clobbered. Returns
   * `undefined` if the model has since been deleted, which is what happens when one is
   * removed while something about it is still being worked out.
   */
  private async amend(
    id: ModelId,
    change: (existing: ModelMeta) => ModelMeta,
  ): Promise<ModelMeta | undefined> {
    try {
      const tx = this.db.transaction(META_STORE, 'readwrite');
      const store = tx.objectStore(META_STORE);

      const existing = await store.get(id);
      if (!existing) {
        await tx.done;
        return undefined;
      }

      const updated = change(existing);
      await store.put(updated);
      await tx.done;

      return updated;
    } catch (error) {
      if (isQuotaError(error)) throw new StorageFullError(error);
      throw error;
    }
  }

  async getFile(id: ModelId): Promise<Blob | undefined> {
    return this.db.get(FILE_STORE, id);
  }

  /**
   * The bytes of an account's copy of a hash, for sending to a peer that lacks it.
   *
   * Scoped to the account deliberately. Two people sharing a browser can hold the same
   * file, and one of them asking for it is not a reason to send the other one's.
   */
  async getFileByHash(hash: string, owner: string): Promise<Blob | undefined> {
    const match = (await this.db.getAll(META_STORE)).find(
      (meta) => meta.hash === hash && (meta.owner ?? NOBODY) === owner,
    );
    return match ? this.getFile(match.id) : undefined;
  }

  /**
   * Remove a model, and remember that it was removed.
   *
   * The tombstone is what makes deletion stick across sessions rather than being undone by
   * the next share. A model with no hash has never been offered to another session, so
   * there is nothing that could bring it back and nothing to record.
   *
   * Deleting an unknown id is not an error.
   */
  async delete(id: ModelId): Promise<void> {
    const meta = await this.db.get(META_STORE, id);

    const tx = this.db.transaction(
      [META_STORE, FILE_STORE, TOMBSTONE_STORE, THUMB_STORE],
      'readwrite',
    );
    const writes: Promise<unknown>[] = [
      tx.objectStore(META_STORE).delete(id),
      tx.objectStore(FILE_STORE).delete(id),
      // In the same transaction as the rest, so a picture cannot outlive the model it is
      // of — and ids are not reused, so an orphan would simply never be collected.
      tx.objectStore(THUMB_STORE).delete(id),
    ];

    if (meta?.hash !== undefined) {
      writes.push(
        tx.objectStore(TOMBSTONE_STORE).put({
          owner: meta.owner ?? NOBODY,
          hash: meta.hash,
          deletedAt: Date.now(),
        }),
      );
    }

    await Promise.all([...writes, tx.done]);
  }

  /**
   * Fill in hashes for records stored before sharing existed.
   *
   * Every file has to be read to be hashed, so this is done once, on demand, rather than
   * inside the version upgrade — a migration that read the entire library would block the
   * viewer from opening at all for anyone with a large one.
   *
   * Each record is written in its own transaction. IndexedDB closes a transaction as soon
   * as the event loop runs dry, and reading a blob and hashing it is exactly the sort of
   * pause that empties it, so holding one open across the whole batch would abort.
   *
   * Returns how many records were filled in.
   */
  async ensureHashes(): Promise<number> {
    const pending = (await this.db.getAll(META_STORE)).filter(
      (meta) => meta.hash === undefined,
    );

    let filled = 0;
    for (const meta of pending) {
      const blob = await this.getFile(meta.id);
      if (!blob) continue;

      const hash = await hashBytes(await blob.arrayBuffer());

      // Re-read inside the write transaction: the model may have been deleted, or given a
      // colour, while its file was being hashed.
      const tx = this.db.transaction(META_STORE, 'readwrite');
      const store = tx.objectStore(META_STORE);
      const current = await store.get(meta.id);
      if (current && current.hash === undefined) {
        await store.put({ ...current, hash });
        filled += 1;
      }
      await tx.done;
    }

    return filled;
  }

  /** How many models belong to no account yet, which is what adoption would claim. */
  async countUnowned(): Promise<number> {
    return (await this.db.getAll(META_STORE)).filter((meta) => meta.owner === undefined)
      .length;
  }

  /**
   * Claim every unattributed model and deletion for an account.
   *
   * Models added before accounts existed, or while signed out, belong to nobody. They are
   * the whole of most people's libraries at the point this feature arrives, so excluding
   * them permanently would make sharing useless; claiming them silently would attach a
   * stranger's library to whichever account signed in on a borrowed machine. So it happens
   * once, deliberately, with the count shown first.
   *
   * Returns how many models were claimed.
   */
  async adopt(owner: string): Promise<number> {
    const tx = this.db.transaction([META_STORE, TOMBSTONE_STORE], 'readwrite');
    const metas = tx.objectStore(META_STORE);
    const stones = tx.objectStore(TOMBSTONE_STORE);

    let claimed = 0;
    for (const meta of await metas.getAll()) {
      if (meta.owner !== undefined) continue;
      await metas.put({ ...meta, owner });
      claimed += 1;
    }

    // Deletions from the same unattributed past. Without these, adopting a library would
    // resurrect everything its owner had already thrown away.
    for (const stone of await stones.getAll()) {
      if (stone.owner !== NOBODY) continue;
      await stones.delete([NOBODY, stone.hash]);

      // The account may already have deleted the same file itself, under its own name.
      // Keep whichever deletion happened later, so adoption cannot move a tombstone
      // backwards in time and lose to an addition it ought to beat.
      const existing = await stones.get([owner, stone.hash]);
      if (existing && existing.deletedAt >= stone.deletedAt) continue;
      await stones.put({ owner, hash: stone.hash, deletedAt: stone.deletedAt });
    }

    await tx.done;
    return claimed;
  }

  /**
   * What this session would tell the account's other sessions it holds.
   *
   * Only models attributed to the account, and only ones with a hash — a model that cannot
   * be identified cannot be reconciled. Everything else in the library stays local and
   * unmentioned.
   */
  async manifestFor(owner: string): Promise<Manifest> {
    const [metas, stones] = await Promise.all([
      this.db.getAll(META_STORE),
      this.db.getAll(TOMBSTONE_STORE),
    ]);

    const models: ManifestEntry[] = [];
    for (const meta of metas) {
      if (meta.owner !== owner || meta.hash === undefined) continue;
      models.push({
        hash: meta.hash,
        name: meta.name,
        byteSize: meta.byteSize,
        addedAt: meta.createdAt,
        ...presentationOf(meta),
      });
    }

    const tombstones: Tombstone[] = stones
      .filter((stone) => stone.owner === owner)
      .map((stone) => ({ hash: stone.hash, deletedAt: stone.deletedAt }));

    return { models, tombstones };
  }

  /**
   * Record deletions decided elsewhere, removing any local copy.
   *
   * Returns the ids removed, so that a viewer currently displaying one of them can let go
   * of it rather than rendering a model that no longer exists.
   */
  async applyTombstones(
    tombstones: readonly Tombstone[],
    owner: string,
  ): Promise<ModelId[]> {
    if (tombstones.length === 0) return [];

    const tx = this.db.transaction(
      [META_STORE, FILE_STORE, TOMBSTONE_STORE, THUMB_STORE],
      'readwrite',
    );
    const metas = tx.objectStore(META_STORE);
    const files = tx.objectStore(FILE_STORE);
    const stones = tx.objectStore(TOMBSTONE_STORE);
    const thumbs = tx.objectStore(THUMB_STORE);

    const doomed = new Map<string, Tombstone>();
    for (const stone of tombstones) doomed.set(stone.hash, stone);

    const removed: ModelId[] = [];
    for (const meta of await metas.getAll()) {
      const stone = meta.hash === undefined ? undefined : doomed.get(meta.hash);
      // Somebody else's copy of the same file is not ours to delete.
      if (!stone || meta.owner !== owner) continue;

      await metas.delete(meta.id);
      await files.delete(meta.id);
      await thumbs.delete(meta.id);
      removed.push(meta.id);
    }

    for (const stone of doomed.values()) {
      const existing = await stones.get([owner, stone.hash]);
      // Keep the later of the two. A tombstone that went backwards would report a stale
      // time at the next share and could lose to an addition it ought to beat.
      if (existing && existing.deletedAt >= stone.deletedAt) continue;
      await stones.put({ owner, hash: stone.hash, deletedAt: stone.deletedAt });
    }

    await tx.done;
    return removed;
  }

  close(): void {
    this.db.close();
  }
}

function newId(): ModelId {
  // randomUUID needs a secure context, which is also what WebXR needs, so in practice it
  // is always there. The fallback keeps unit tests and plain-HTTP pokes about working.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `model-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function isQuotaError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'QuotaExceededError';
}

/**
 * The DOM types declare `navigator.storage` as always present. It is not: it is absent in
 * insecure contexts and in older browsers, so this restates it as the optional thing it
 * actually is rather than trusting the optimistic declaration.
 */
interface NavigatorWithOptionalStorage {
  readonly storage?: StorageManager;
}

function storageManager(): StorageManager | undefined {
  const optimistic: NavigatorWithOptionalStorage = navigator;
  return optimistic.storage;
}

/**
 * Ask the browser to exempt this origin from routine eviction.
 *
 * Best-effort: browsers may grant it silently, prompt, or ignore it entirely, and a
 * refusal is not worth bothering the user about.
 */
export async function requestPersistence(): Promise<boolean> {
  const storage = storageManager();
  if (!storage) return false;

  try {
    return await storage.persist();
  } catch {
    return false;
  }
}

export async function estimateUsage(): Promise<StorageUsage | undefined> {
  const storage = storageManager();
  if (!storage) return undefined;

  try {
    const { usage, quota } = await storage.estimate();
    return { usedBytes: usage ?? 0, quotaBytes: quota };
  } catch {
    return undefined;
  }
}

/**
 * An amendment reduced to what this version is willing to store.
 *
 * The wire layer has already checked these, and they are checked again here. That is not
 * belt and braces for its own sake: this method is the one door into the library for a
 * decision made on another machine, and a value it will not vouch for should not get in
 * through it whatever route it took.
 *
 * A time is kept only alongside the value it belongs to. A time whose value was thrown out
 * would claim recency for a decision that is no longer there, and would then beat the
 * perfectly good local one it was supposed to replace.
 */
function storable(change: Amendment): ModelPresentation {
  const colour = change.colour === undefined ? undefined : normaliseColour(change.colour);
  const roomScale = normaliseRoomScale(change.roomScale);

  return {
    ...(colour === undefined ? {} : { colour }),
    ...(colour === undefined || change.colourAt === undefined
      ? {}
      : { colourAt: change.colourAt }),
    ...(roomScale === undefined ? {} : { roomScale }),
    ...(roomScale === undefined || change.roomScaleAt === undefined
      ? {}
      : { roomScaleAt: change.roomScaleAt }),
  };
}

/** Whether a merged presentation says anything the stored record does not already say. */
function differs(meta: ModelMeta, merged: ModelPresentation): boolean {
  return (
    meta.colour !== merged.colour ||
    meta.colourAt !== merged.colourAt ||
    meta.roomScale !== merged.roomScale ||
    meta.roomScaleAt !== merged.roomScaleAt
  );
}

/**
 * A presentation as a record holds it: only the fields that have a value.
 *
 * A stored record's optional fields mean "not set" by their absence, never by holding an
 * explicit undefined, so spreading one straight in would be writing the wrong thing —
 * which the type checker says here rather than leaving it to be discovered.
 */
function recorded(
  presentation: ModelPresentation,
): Partial<Pick<ModelMeta, 'colour' | 'colourAt' | 'roomScale' | 'roomScaleAt'>> {
  return {
    ...(presentation.colour === undefined ? {} : { colour: presentation.colour }),
    ...(presentation.colourAt === undefined ? {} : { colourAt: presentation.colourAt }),
    ...(presentation.roomScale === undefined
      ? {}
      : { roomScale: presentation.roomScale }),
    ...(presentation.roomScaleAt === undefined
      ? {}
      : { roomScaleAt: presentation.roomScaleAt }),
  };
}
